import React, { useEffect, useMemo, useState } from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';
import RouteIcon from '@mui/icons-material/Route';
import { ConfirmationDialogMAIQ } from 'app/components/ReusableUIComponents/LowLevelComponents/ConfirmationDialogMAIQ';
import {
  HeaderDropdownMAIQ,
  HeaderDropdownMenuItemMAIQ,
} from 'app/components/ReusableUIComponents/LowLevelComponents/HeaderDropdownMAIQ';
import { useGuidanceTour } from '../GuidanceTourContext';
import { GuidanceTourConfig, TourProgressSummary } from '../types';
import { readLauncherProgress } from '../utils/tourStorage';
import { isApiDrivenTour, readLauncherProgressFromApi } from '../utils/tourApiStorage';
import OnboardingLaunchDealerButton from './OnboardingLaunchDealerButton';

interface GuidanceTourLauncherProps {
  config: GuidanceTourConfig;
  dealerId: string;
}

export const GuidanceTourLauncher: React.FC<GuidanceTourLauncherProps> = ({
  config,
  dealerId,
}) => {
  const {
    isActive,
    isStartingTour,
    startTour,
    continueTour,
    tourStorageRevision,
  } = useGuidanceTour();

  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [restartConfirmOpen, setRestartConfirmOpen] = useState(false);
  const [launcherProgress, setLauncherProgress] = useState<{
    progress: TourProgressSummary;
    hasProgress: boolean;
  }>({
    progress: {
      completed: 0,
      pending: config.steps.length,
      total: config.steps.length,
      percent: 0,
      nextIncompleteIndex: 0,
    },
    hasProgress: false,
  });

  useEffect(() => {
    let cancelled = false;

    const loadProgress = async () => {
      const result = isApiDrivenTour(config.id)
        ? await readLauncherProgressFromApi(config.id, dealerId, config)
        : readLauncherProgress(config.id, dealerId, config);

      if (!cancelled) {
        setLauncherProgress({
          progress: result.progress,
          hasProgress: result.hasProgress,
        });
      }
    };

    void loadProgress();

    return () => {
      cancelled = true;
    };
  }, [config, dealerId, tourStorageRevision]);

  const { progress, hasProgress } = useMemo(
    () => launcherProgress,
    [launcherProgress],
  );

  const progressPercent = progress?.percent ?? 0;

  const primaryLabel = hasProgress
    ? `Launch Readiness · ${progressPercent}%`
    : 'Launch Readiness · Start';

  const handleStartClick = () => {
    void startTour({ config, dealerId, startAtStep: 0, resetProgress: true });
  };

  const handleContinueClick = () => {
    void continueTour(config, dealerId);
  };

  const handleRestart = () => {
    setRestartConfirmOpen(false);
    setMenuAnchor(null);
    void startTour({ config, dealerId, startAtStep: 0, resetProgress: true });
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setMenuAnchor(event.currentTarget);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
  };

  if (isActive) return null;

  const showLaunchDealerButton = hasProgress && progressPercent === 100;

  return (
    <>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexShrink: 0 }}>
        {showLaunchDealerButton ? (
          <OnboardingLaunchDealerButton dealerId={dealerId} />
        ) : null}
        <HeaderDropdownMAIQ
          id="launch-readiness"
          disabled={isStartingTour}
          label={
            <>
              {isStartingTour ? (
                <CircularProgress size={16} sx={{ color: 'white', flexShrink: 0 }} />
              ) : (
                <RouteIcon sx={{ fontSize: 18, flexShrink: 0 }} />
              )}
              {primaryLabel}
            </>
          }
          richLabel
          open={Boolean(menuAnchor)}
          anchorEl={menuAnchor}
          onOpen={handleMenuOpen}
          onClose={handleMenuClose}
          openOnTrigger={false}
          openOnChevronOnly={hasProgress}
          onTriggerClick={hasProgress ? handleContinueClick : handleStartClick}
          minWidth={220}
          menuMinWidth={280}
          aria-label="Launch Readiness"
        >
          {hasProgress ? (
            <>
              <HeaderDropdownMenuItemMAIQ disabled>
                <Typography variant="caption" color="text.secondary">
                  Launch Readiness progress: {progressPercent}% ·{' '}
                  {progress?.completed ?? 0} of {progress?.total ?? 0} steps complete
                </Typography>
              </HeaderDropdownMenuItemMAIQ>
              <HeaderDropdownMenuItemMAIQ
                onClick={() => {
                  handleMenuClose();
                  void continueTour(config, dealerId);
                }}
              >
                Continue where I left off
              </HeaderDropdownMenuItemMAIQ>
              <HeaderDropdownMenuItemMAIQ
                onClick={() => {
                  handleMenuClose();
                  setRestartConfirmOpen(true);
                }}
              >
                Start from beginning
              </HeaderDropdownMenuItemMAIQ>
            </>
          ) : null}
        </HeaderDropdownMAIQ>
      </Box>

      <ConfirmationDialogMAIQ
        open={restartConfirmOpen}
        onClose={() => setRestartConfirmOpen(false)}
        onConfirm={handleRestart}
        title="Restart Launch Readiness?"
        message="All saved step progress for this dealership will be reset."
        confirmLabel="Restart"
        loading={isStartingTour}
        loadingLabel="Restarting..."
        confirmColor="primary"
        cancelColor="primary"
      />
    </>
  );
};

export default GuidanceTourLauncher;
